import { useMemo } from 'react';

import type { TechnologyFilterOptions } from '@/hooks/api/useGetTechnologyFilters';
import type { Filter } from '@/hooks/useTechnology';
import { reconcileMergedFilterValues } from '@/utils/mergeFilterOptions.util';

/** Merged filter keys reconciled via the options-driven merged utility
 * (mergeFilterOptions.util.ts). Tags keep the raw-value contract and are
 * never reconciled. */
const MERGED_RECONCILE_KEYS = ['vendor', 'product', 'version'] as const;

/**
 * Derives the effective technology filter by reconciling persisted merged-key
 * filters — vendor/product/version (ENG-4329) — against the CURRENT filter
 * options so the exact-match IN-query matches every raw variant of the
 * selected labels (pre-fix persisted filters may hold a single raw variant).
 * Stored state is intentionally untouched: this hook is DERIVE-ONLY and never
 * writes the reconciled values back to storage (P0-5). Mirrors
 * {@link useEffectiveSeedFilter} (sections/seeds/hooks).
 *
 * Superset semantics: {@link reconcileMergedFilterValues} returns the UNION
 * of the stored values and their collapse→re-expansion against the current
 * options, so the effective filter can only ever match MORE rows than the
 * stored filter, never fewer.
 *
 * Fallback while options load or errored (`filterOptionsStatus !==
 * 'success'`, P2-2) — or when no reconcilable key has values — is to pass the
 * stored `filter` through (today's behavior). The early returns deliberately
 * return the SAME `filter` reference (no cloning), so downstream
 * referential-identity checks are unaffected when no reconciliation applies.
 *
 * @param filter - The current technology filter state
 * @param technologyFilterOptions - Filter options from
 *   {@link useGetTechnologyFilters}
 * @param filterOptionsStatus - Load status of the filter options query
 * @returns The reconciled filter, or the stored `filter` reference when no
 *   reconciliation applies
 */
export function useEffectiveTechnologyFilter(
  filter: Filter,
  technologyFilterOptions: TechnologyFilterOptions,
  filterOptionsStatus: 'error' | 'success' | 'pending'
): Filter {
  return useMemo<Filter>(() => {
    if (filterOptionsStatus !== 'success') return filter;

    let reconciled: Record<string, string[]> | null = null;

    for (const key of MERGED_RECONCILE_KEYS) {
      const stored = filter.properties?.[key];
      const options = technologyFilterOptions?.[key];
      if (stored && stored.length > 0 && options) {
        reconciled = {
          ...(reconciled ?? {}),
          [key]: reconcileMergedFilterValues(stored, options),
        };
      }
    }

    if (!reconciled) return filter;
    return {
      ...filter,
      properties: { ...filter.properties, ...reconciled },
    };
  }, [filter, technologyFilterOptions, filterOptionsStatus]);
}
